import { Link, useParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import CastList from "@/pages/detail/components/CastList";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import RelatedDramas from "@/pages/detail/components/RelatedDramas";
import { dramas } from "@/mocks/dramas";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const drama = dramas.find((item) => item.id === id);

  useDocumentMeta({
    title: drama ? `${drama.title}剧情介绍与演员名单 - 出轨的味道影视剧` : "影视剧详情 - 出轨的味道影视剧",
    description: drama
      ? `详细介绍${drama.title}的剧情梗概、主演介绍和相关新闻，了解出轨的味道相关影视内容。`
      : "出轨的味道影视剧详情页。",
    keywords: drama
      ? `${drama.title},出轨的味道,${drama.title}剧情介绍,${drama.title}演员名单,${drama.genres.join(",")}`
      : "出轨的味道,影视剧详情",
  });

  if (!drama) {
    return (
      <AppLayout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这部影视剧</h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的内容可能已经下线，或者链接有误。</p>
          <Link
            to="/category"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回影视分类
          </Link>
        </div>
      </AppLayout>
    );
  }

  const infoItems = [
    { label: "类型", value: drama.genres.join(" / "), icon: "ri-bookmark-3-line" },
    { label: "制作年份", value: `${drama.year} 年`, icon: "ri-calendar-line" },
    { label: "剧集状态", value: drama.status, icon: "ri-play-circle-line" },
    { label: "集数", value: `全 ${drama.episodeCount} 集`, icon: "ri-list-check-2" },
    { label: "评分", value: drama.rating.toFixed(1), icon: "ri-star-line" },
    { label: "热度", value: `${drama.views} 次观看`, icon: "ri-fire-line" },
  ];

  const highlights = [
    "以一段婚姻的裂缝为起点，逐步揭开人物之间被长期掩盖的关系真相。",
    "叙事采用线索递进的方式，日常细节里埋下大量可作为解读依据的伏笔。",
    "配角群像塑造立体，每个人物都有自己的立场与隐情，没有绝对的对错。",
    "情感浓度与悬念节奏并重，适合喜欢沉浸式追剧与深度讨论的观众。",
  ];

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-8 md:py-10">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-accent-700 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/category" className="transition-colors hover:text-accent-700 cursor-pointer">
            影视分类
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{drama.title}</span>
        </nav>

        {/* Hero */}
        <div className="mt-5 overflow-hidden rounded-lg border border-background-200 bg-primary-950">
          <div className="flex flex-col gap-6 p-5 md:flex-row md:items-center md:gap-8 md:p-8">
            <div className="mx-auto w-40 shrink-0 overflow-hidden rounded-lg bg-background-900 md:mx-0 md:w-56">
              <div className="w-full" style={{ aspectRatio: "2 / 3" }}>
                <img
                  src={drama.poster}
                  alt={`${drama.title} 主视觉海报`}
                  title={`${drama.title} 剧情介绍与演员名单`}
                  className="h-full w-full object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-primary-950">
                  {drama.status}
                </span>
                {drama.featured && (
                  <span className="flex items-center gap-1 rounded-md bg-background-50/10 px-2.5 py-1 text-[11px] text-accent-400">
                    <i className="ri-fire-fill"></i>主推作品
                  </span>
                )}
              </div>
              <h1 className="mt-3 font-heading text-3xl font-bold text-background-50 md:text-4xl">{drama.title}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-50/70">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400"></i>
                  评分 {drama.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {drama.year}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-list-check-2"></i>
                  全 {drama.episodeCount} 集
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line"></i>
                  {drama.views}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {drama.genres.map((genre) => (
                  <span key={genre} className="rounded-full bg-background-50/10 px-3 py-1 text-[11px] text-background-50/80">
                    {genre}
                  </span>
                ))}
              </div>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-50/85">{drama.synopsis}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="#episodes"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer"
                >
                  <i className="ri-list-check-2 text-lg"></i>
                  查看剧集目录
                </a>
                <Link
                  to="/news"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
                >
                  <i className="ri-newspaper-line text-lg"></i>
                  相关资讯
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* 剧情介绍 */}
        <section className="mt-12">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-book-open-line text-lg"></i>
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-950">剧情介绍</h2>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <p className="text-sm leading-7 text-foreground-700">{drama.synopsis}</p>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                《{drama.title}》以细腻的日常描写切入，把镜头对准人物之间那些难以言说的时刻。故事并不急于给出判断，
                而是通过一次次对话、一个个细节，让观众自己拼凑出真相的全貌。{drama.cast[0]?.name}饰演的
                {drama.cast[0]?.role}与{drama.cast[1]?.name}饰演的{drama.cast[1]?.role}之间的对手戏，
                构成了全剧最有张力的部分。
              </p>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                随着剧情推进，越来越多看似无关的线索被串联起来，人物的动机也逐渐清晰。{drama.status === "已完结"
                  ? "全剧已完结，可以一次性追完整个故事。"
                  : drama.status === "更新中"
                  ? "剧集仍在持续更新中，后续走向值得期待。"
                  : "剧集即将上线，敬请关注后续播出消息。"}
              </p>

              <h3 className="mt-6 font-heading text-base font-bold text-foreground-950">剧情看点</h3>
              <ul className="mt-3 flex flex-col gap-2.5">
                {highlights.map((text) => (
                  <li key={text} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-accent-600"></i>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="self-start rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">剧集信息</h3>
              <dl className="mt-4 flex flex-col gap-3.5">
                {infoItems.map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-3 border-b border-background-200 pb-3 last:border-none last:pb-0">
                    <dt className="flex items-center gap-2 text-xs text-foreground-500">
                      <i className={`${item.icon} text-accent-600`}></i>
                      {item.label}
                    </dt>
                    <dd className="text-right text-xs font-medium text-foreground-900">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </aside>
          </div>
        </section>

        {/* 演员名单 */}
        <div className="mt-12">
          <CastList cast={drama.cast} />
        </div>

        {/* 剧集目录 */}
        <div id="episodes" className="mt-12 scroll-mt-24">
          <EpisodeList drama={drama} />
        </div>

        {/* 相关推荐 */}
        <div className="mt-12">
          <RelatedDramas drama={drama} />
        </div>
      </div>
    </AppLayout>
  );
}